/**
 * S1 — Client Dashboard (`/`). One scrolling page, three stacked panels:
 *   1. Agreed program  (read-only converted-offer reference)
 *   2. Week-by-week status  (period selector + planned-vs-actual chart)
 *   3. My invoices  (read-only, per-row download)
 *
 * Honest states everywhere (HP-EXP-1): a missing source renders a loud
 * "not available" / empty notice — never fabricated rows.
 *
 * Spec: teivor-build-spec/08-ui-spec.md §S1. Role: `client` only (the portal
 * shell routes `client_salesman` away — not handled here).
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  EmptyState,
  InlineAlert,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  toast,
  type DataTableColumn,
} from '@teivor/shared-ui';
import { formatCurrency, formatDate } from '@teivor/shared-utils';

import { PageContainer } from '../../../components/PageContainer';
import { useDashboardQuery, useDownloadInvoiceMutation } from '../queries';
import type { DashboardPeriod, InvoiceRecord } from '../types';

import { WeekByWeekChart } from './WeekByWeekChart';

function useLocale(): 'cs-CZ' | 'en-US' {
  const { i18n } = useTranslation();
  return i18n.language?.startsWith('en') ? 'en-US' : 'cs-CZ';
}

// ─── Panel: Agreed program ──────────────────────────────────────────────────

function AgreedProgramPanel({
  available,
  versionRef,
}: {
  available: boolean;
  versionRef: string | null;
}) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('com.dashboard.program.title', 'Sjednaný program / Agreed program')}</CardTitle>
      </CardHeader>
      <CardContent>
        {available ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Badge variant="success">{t('com.dashboard.program.agreed', 'Sjednáno / Agreed')}</Badge>
              {versionRef ? (
                <span
                  className="text-[12px] leading-[16px] text-[var(--tvl-ink-3)]"
                  style={{ fontFamily: 'var(--tvl-font-mono)' }}
                >
                  {versionRef}
                </span>
              ) : null}
            </div>
            {/* The dashboard API exposes only the converted-funnel reference, not the
                itemised offer (program size, destinations, pricing). Surfaced honestly
                rather than fabricated — see orchestrator note. */}
            <InlineAlert variant="info">
              {t(
                'com.dashboard.program.detail_pending',
                'Podrobný přehled programu zpřístupníme, jakmile bude napojen. / The itemised program summary will appear once its source is wired.',
              )}
            </InlineAlert>
          </div>
        ) : (
          <EmptyState
            variant="empty"
            title={t('com.dashboard.program.pending', 'Program se připravuje / Your program is being set up')}
            description={t(
              'com.dashboard.program.pending_desc',
              'Sjednaný program se zobrazí, jakmile bude potvrzena vaše nabídka. / Your agreed program will appear once your offer is confirmed.',
            )}
          />
        )}
      </CardContent>
    </Card>
  );
}

// ─── Panel: Week-by-week status ─────────────────────────────────────────────

const PERIODS: DashboardPeriod[] = ['daily', 'weekly', 'monthly'];

function WeekByWeekStatusPanel({
  period,
  onPeriodChange,
  available,
  weeks,
}: {
  period: DashboardPeriod;
  onPeriodChange: (p: DashboardPeriod) => void;
  available: boolean;
  weeks: { weekStart: string; planned: number; actual: number }[];
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const periodLabel = (p: DashboardPeriod) =>
    p === 'daily'
      ? t('com.dashboard.period.daily', 'Denně / Daily')
      : p === 'weekly'
        ? t('com.dashboard.period.weekly', 'Týdně / Weekly')
        : t('com.dashboard.period.monthly', 'Měsíčně / Monthly');

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-4">
        <CardTitle>{t('com.dashboard.status.title', 'Týdenní stav / Week-by-week status')}</CardTitle>
        <div className="w-44">
          <Select value={period} onValueChange={(v) => onPeriodChange(v as DashboardPeriod)} aria-label={t('com.dashboard.status.period', 'Období / Period')}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIODS.map((p) => (
                <SelectItem key={p} value={p}>
                  {periodLabel(p)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {!available ? (
          <InlineAlert variant="warning">
            {t('com.dashboard.status.unavailable', 'Data nejsou k dispozici / Data not available')}
          </InlineAlert>
        ) : weeks.length === 0 ? (
          <EmptyState
            variant="empty"
            title={t('com.dashboard.status.setup', 'Nastavení probíhá / Setup in progress')}
            description={t(
              'com.dashboard.status.setup_desc',
              'Plánované týdny se zobrazí, jakmile začne kampaň. / Planned weeks appear once the campaign starts.',
            )}
          />
        ) : (
          <WeekByWeekChart
            weeks={weeks}
            labelFor={(weekStart) => formatDate(weekStart, locale)}
            plannedLabel={t('com.dashboard.status.planned', 'Plán / Planned')}
            actualLabel={t('com.dashboard.status.actual', 'Skutečnost / Actual')}
          />
        )}
      </CardContent>
    </Card>
  );
}

// ─── Panel: My invoices ─────────────────────────────────────────────────────

function MyInvoicesPanel({ records }: { records: InvoiceRecord[] }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const download = useDownloadInvoiceMutation();

  const onDownload = React.useCallback(
    (id: string) => {
      download.mutate(id, {
        onSuccess: (res) => {
          if (res?.url) window.open(res.url, '_blank', 'noopener');
          else
            toast.info(
              t('com.dashboard.invoices.download_unavailable', 'Stahování zatím není k dispozici / Download not available yet'),
            );
        },
        onError: () =>
          toast.error(
            t('com.dashboard.invoices.download_unavailable', 'Stahování zatím není k dispozici / Download not available yet'),
          ),
      });
    },
    [download, t],
  );

  const columns: DataTableColumn<InvoiceRecord>[] = [
    {
      id: 'number',
      header: t('com.dashboard.invoices.number', 'Číslo / Number'),
      cell: (r) => (
        <span className="tabular-nums" style={{ fontFamily: 'var(--tvl-font-mono)' }}>
          {r.number}
        </span>
      ),
      primary: true,
    },
    {
      id: 'issuedAt',
      header: t('com.dashboard.invoices.issued', 'Vystaveno / Issued'),
      cell: (r) => <span className="tabular-nums">{formatDate(r.issuedAt, locale)}</span>,
    },
    {
      id: 'amount',
      header: t('com.dashboard.invoices.amount', 'Částka / Amount'),
      cell: (r) => <span className="tabular-nums">{formatCurrency(r.amount, r.currency, locale)}</span>,
    },
    {
      id: 'download',
      header: '',
      cell: (r) => (
        <Button
          variant="tertiary"
          size="sm"
          onClick={() => onDownload(r.id)}
          loading={download.isPending && download.variables === r.id}
        >
          {t('com.dashboard.invoices.download', 'Stáhnout / Download')}
        </Button>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('com.dashboard.invoices.title', 'Moje faktury / My invoices')}</CardTitle>
      </CardHeader>
      <CardContent>
        {records.length === 0 ? (
          <EmptyState
            variant="empty"
            title={t('com.dashboard.invoices.empty', 'Zatím žádné faktury / No invoices yet')}
          />
        ) : (
          <DataTable<InvoiceRecord>
            id="com-invoices"
            data={records}
            columns={columns}
            getRowId={(r) => r.id}
          />
        )}
      </CardContent>
    </Card>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export function ClientDashboardPage() {
  const { t } = useTranslation();
  const [period, setPeriod] = React.useState<DashboardPeriod>('weekly');
  const { data, isLoading, isError, refetch } = useDashboardQuery(period);

  return (
    <PageContainer width="default">
      <header>
        <h1 className="text-[22px] font-semibold leading-[28px] text-[var(--tvl-ink)] md:text-[26px]">
          {t('com.dashboard.title', 'Přehled / Dashboard')}
        </h1>
      </header>

      {isLoading ? (
        <div className="flex flex-col gap-6">
          <Skeleton className="h-40" />
          <Skeleton className="h-64" />
          <Skeleton className="h-48" />
        </div>
      ) : isError || !data ? (
        <EmptyState
          variant="error-loading"
          title={t('com.dashboard.error', 'Přehled se nepodařilo načíst / Could not load your dashboard')}
          action={
            <Button variant="secondary" size="sm" onClick={() => void refetch()}>
              {t('common.retry', 'Zkusit znovu / Retry')}
            </Button>
          }
        />
      ) : (
        <>
          <AgreedProgramPanel
            available={data.funnel.available}
            versionRef={data.funnel.convertedFunnelVersionRef}
          />
          <WeekByWeekStatusPanel
            period={period}
            onPeriodChange={setPeriod}
            available={data.liveStatus.available}
            weeks={data.liveStatus.weeks}
          />
          <MyInvoicesPanel records={data.invoices.available ? data.invoices.records : []} />
        </>
      )}
    </PageContainer>
  );
}
